// packages import
import { yupResolver } from "@hookform/resolvers/yup"
import { Box, Button, CircularProgress } from "@mui/material"
import { FC, Reducer, useContext, useEffect, useReducer, useState } from "react"
import { FormProvider, SubmitHandler, useForm } from "react-hook-form"
// component import
import { AddNewJudge } from "../../cases/createNewCase/AddNewJudge"
import { Alert } from "../../common/Alert"
import { CustomModal } from "../../common/CustomModal"
import { JudgeAutoComplete } from "../../common/JudgeAutoComplete"
// others
import { AssignExternalUserToCourt, ExternalUser, ExternalUserRoleRole, Maybe, useAssignCourtToExternalUserMutation, useFetchAllExternalUsersLazyQuery } from "../../../../generated"
import { CONFLICT_EXCEPTION, GRAPHQL_QUERY_POLICY, SOMETHING_WENT_WRONG } from "../../../constants"
import { AppContext } from "../../../context"
import { ActionType } from "../../../context/AppContextReducer"
import { AddJudgeModalProps, AssignCourtToExternalUserInput } from "../../../interfaceTypes"
import { addJudgeValidationSchema } from "../../../validationSchema"
import { CaseAction, CaseActionType, CaseState, caseInitialState, caseReducer } from "../../cases/caseReducer"

export const AddJudge: FC<AddJudgeModalProps> = ({ isOpen, courtId, reFetchCourt, judgesData, refetchExistingJudges }) => {
  const [caseState, caseDispatch] = useReducer<Reducer<CaseState, CaseAction>>(caseReducer, caseInitialState)
  const { dispatch } = useContext(AppContext)
  const [newExternalUser, setNewExternalUser] = useState<ExternalUser>()
  const [externalUsersData, setExternalUsersData] = useState<(Maybe<ExternalUser> | undefined)[]>()
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [currentTotalPages, setCurrentTotalPages] = useState<number>(1);

  const [fetchAllExternalUsers, { refetch: refetchAllExternalUsers }] = useFetchAllExternalUsersLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,

    onError() {
      setExternalUsersData([])
    },

    onCompleted(data) {
      if (data) {
        const { fetchAllExternalUsers } = data

        if (fetchAllExternalUsers) {
          const { externalUsers, response, pagination } = fetchAllExternalUsers
          const { status } = response || {}

          if (status === 200 && externalUsers) {
            if (currentTotalPages >= currentPage) {
              externalUsersData ? setExternalUsersData([...externalUsersData, ...externalUsers]) : setExternalUsersData(externalUsers)
            }
            setExternalUsersData(externalUsers)
          }

          if (pagination) {
            const { totalPages } = pagination

            if (totalPages && totalPages > 0) {
              setCurrentTotalPages(totalPages)
              setCurrentPage(currentPage + 1)
            }
          }
        }
      }
    },
  })

  useEffect(() => {
    if (currentTotalPages >= currentPage) {
      fetchAllExternalUsers({
        variables: {
          externalUsersInput: {
            roles: [ExternalUserRoleRole.Judge],

            paginationOptions: {
              limit: 1000,
              page: currentPage
            }
          }
        }
      })
    }
    // eslint-disable-next-line
  }, [currentTotalPages, currentPage])

  const methods = useForm<AssignCourtToExternalUserInput>({
    mode: 'all',
    resolver: yupResolver(addJudgeValidationSchema),
    defaultValues: {
      judgeUser: { id: "", email: "", firstName: "", lastName: "" },
    }
  })

  const { reset, handleSubmit, setValue: setFormValue } = methods
  const handModelNewClient = () => {
    caseDispatch({ type: CaseActionType.SET_NEW_jUDGE_OPEN, isNewJudgeOpen: true })
  }

  const [assignCourtToExternalUser, { loading: assignCourtToExternalUserLoading }] = useAssignCourtToExternalUserMutation({
    onError(error) {
      if (error.message === CONFLICT_EXCEPTION) Alert.error('Judge already assigned to this court')
      else Alert.error(SOMETHING_WENT_WRONG)
    },

    onCompleted(data) {
      if (data) {
        const { assignCourtToExternalUser } = data

        if (assignCourtToExternalUser) {
          const { response } = assignCourtToExternalUser
          const { status } = response || {}

          if (status === 200) {
            Alert.success('Judge assigned to the court')
            reset()
            refetchExistingJudges && refetchExistingJudges()
            dispatch({ type: ActionType.SET_CURRENT_PAGE, currentPage: 1 })
            dispatch({ type: ActionType.SET_IS_OPEN, isOpen: !isOpen })
            reFetchCourt && reFetchCourt()
          }
        }
      }
    }
  })

  const onSubmit: SubmitHandler<AssignExternalUserToCourt> = async (data: AssignExternalUserToCourt) => {
    const { externalUserId } = data
    externalUserId ?
      await assignCourtToExternalUser({
        variables: {
          assignExternalUserToCourt: {
            courtId: courtId!,
            externalUserId
          }
        }
      })
      :
      Alert.error('Select judge')
  }

  const onChangeHandle = (id: string) => setFormValue('externalUserId', id)
  const handleCloseModal = () => {
    dispatch({ type: ActionType.SET_IS_OPEN, isOpen: !isOpen })
    reset()
  }

  useEffect(() => {
    const { id, firstName, lastName, email } = newExternalUser || {}
    setFormValue("judgeUser", { id: id!, firstName: firstName!, lastName: lastName!, email: email! })
    setFormValue('externalUserId', id!)
  }, [newExternalUser, setFormValue])

  return (
    <CustomModal title='Add judge to court' isOpen={isOpen} handleClose={handleCloseModal}>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
          <JudgeAutoComplete
            onChangeHandle={onChangeHandle}
            handModelNewClient={handModelNewClient}
            courtId={courtId}
            judgesData={judgesData}
          />

          <Box display='flex' justifyContent='flex-end' mt={2} mb={3}>
            <Button
              variant="contained"
              type="submit"
              color="primary"
              disabled={assignCourtToExternalUserLoading}
              endIcon={assignCourtToExternalUserLoading && <CircularProgress size={20} color="inherit" />}
              aria-label='add judge'
            >
              Add
            </Button>
          </Box>
        </form>
      </FormProvider>

      <AddNewJudge
        state={caseState}
        dispatch={caseDispatch}
        refetch={refetchAllExternalUsers}
        clientType={ExternalUserRoleRole.Judge}
        setNewExternalUser={setNewExternalUser}
      />
    </CustomModal>
  )
}